/**
 * Holds serialized Monkey 1v1 challenge payloads for guests (unauthenticated).
 *
 * SECURITY / PRODUCT: Guest flow must not write to the database. This store persists only to
 * localStorage (web) / AsyncStorage (native). The backend is called after sign-in from
 * PendingGuestMonkeyChallengeFlush → flushPendingGuestMonkeyChallenge (FIFO queue).
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { PickPayload } from '../../create/helpers/createChallenge.helper';
import type { GameKitConfig, OddsMarketSnapshot } from '../../create/types/create.types';
import { logger } from '../../helpers/logger.helper';
import { platformStorage } from './platformStorage';

/** Snapshot of team names + kits for Arena preview (no DB until sign-in). */
export interface PendingGuestMatchDisplay {
  homeTeam: string;
  awayTeam: string;
  league: string;
  dateIso?: string;
  homeKit?: GameKitConfig;
  awayKit?: GameKitConfig;
  /** 1X2 decimal odds from Create match list (optional on legacy persisted slips). */
  homeOdd?: number;
  drawOdd?: number;
  awayOdd?: number;
  /** When the matches API sent odds.markets — used for Monkey preview on BTTS, O/U, etc. */
  oddsMarkets?: OddsMarketSnapshot[];
}

/** Built on Create; {@link localId} added when enqueueing. */
export type PendingGuestMonkeyChallengeBuilt = {
  challengeName: string;
  opponentUserId: string;
  matchIds: string[];
  picks: PickPayload[];
  stakeCoins: number;
  /** Latest estimated end across all matches (kickoff + 110 min). Undefined = legacy / unknown. */
  latestEstimatedEndMs?: number;
  /** ISO timestamp when the slip was queued. Used for 48 h orphan TTL. */
  enqueuedAt?: string;
  matchDisplayById: Record<string, PendingGuestMatchDisplay>;
};

export interface PendingGuestMonkeyChallengePayload extends PendingGuestMonkeyChallengeBuilt {
  localId: string;
}

interface PendingGuestMonkeyChallengeState {
  pendingQueue: PendingGuestMonkeyChallengePayload[];
  enqueuePending: (payload: PendingGuestMonkeyChallengePayload) => void;
  setPendingQueue: (queue: PendingGuestMonkeyChallengePayload[]) => void;
  /** Remove the front slip after successful flush or skip (matches {@link localId}). */
  dequeueAfterFlush: (localId: string) => void;
  /** Not persisted: show Arena login prompt once after guest taps Send → replace to home. */
  arenaLoginPromptPending: boolean;
  setArenaLoginPromptPending: (value: boolean) => void;
  _hasHydrated: boolean;
}

type PersistedPendingShape = {
  state?: {
    pending?: PendingGuestMonkeyChallengePayload | null;
    pendingQueue?: PendingGuestMonkeyChallengePayload[];
  };
};

function migratePersistedToQueue(persisted: unknown): Pick<
  PendingGuestMonkeyChallengeState,
  'pendingQueue'
> {
  if (!persisted || typeof persisted !== 'object') {
    return { pendingQueue: [] };
  }
  const root = persisted as PersistedPendingShape;
  const inner = root.state ?? (persisted as { pendingQueue?: unknown; pending?: unknown });
  if (Array.isArray((inner as { pendingQueue?: unknown }).pendingQueue)) {
    // Strip legacy earliestKickoffMs field; new entries use latestEstimatedEndMs
    const queue = (inner as { pendingQueue: PendingGuestMonkeyChallengePayload[] }).pendingQueue.map(
      ({ earliestKickoffMs: _, ...rest }: PendingGuestMonkeyChallengePayload & { earliestKickoffMs?: number }) => rest,
    );
    return { pendingQueue: queue };
  }
  const single = (inner as { pending?: PendingGuestMonkeyChallengePayload | null }).pending;
  if (single && typeof single === 'object') {
    const { earliestKickoffMs: _, ...row } = single as PendingGuestMonkeyChallengePayload & { earliestKickoffMs?: number };
    const withDisplay = {
      ...row,
      localId: row.localId ?? `migrated_${Date.now()}`,
      matchDisplayById:
        row.matchDisplayById && typeof row.matchDisplayById === 'object'
          ? (row.matchDisplayById as Record<string, PendingGuestMatchDisplay>)
          : {},
    };
    return { pendingQueue: [withDisplay] };
  }
  return { pendingQueue: [] };
}

export const usePendingGuestMonkeyChallengeStore = create<PendingGuestMonkeyChallengeState>()(
  persist(
    (set) => ({
      pendingQueue: [],
      enqueuePending: (payload) =>
        set((s) => ({
          pendingQueue: [...s.pendingQueue, payload],
        })),
      setPendingQueue: (queue) => set({ pendingQueue: queue }),
      dequeueAfterFlush: (localId) =>
        set((s) => {
          const [first, ...rest] = s.pendingQueue;
          if (first?.localId === localId) {
            return { pendingQueue: rest };
          }
          return s;
        }),
      arenaLoginPromptPending: false,
      setArenaLoginPromptPending: (value: boolean) => set({ arenaLoginPromptPending: value }),
      _hasHydrated: false,
    }),
    {
      name: 'zporti_pending_guest_monkey_challenge',
      version: 3,
      storage: createJSONStorage(() => platformStorage),
      partialize: (state) => ({ pendingQueue: state.pendingQueue }),
      migrate: (persisted, _fromVersion) => migratePersistedToQueue(persisted) as never,
      onRehydrateStorage: () => (_state, error) => {
        if (error) {
          logger.error('Failed to hydrate pending guest monkey challenge:', error);
        }
        queueMicrotask(() => {
          usePendingGuestMonkeyChallengeStore.setState({ _hasHydrated: true });
        });
      },
    },
  ),
);
